import axios, { AxiosRequestConfig } from 'axios';

// Constants
import ENVIRONMENTS from 'constants/enviroments';
import { errorMessagersList } from 'helpers/errorMessagers';
import CONST_AUTH from 'constants/auth';
import PATH from 'constants/basePath';
import { ERROR_CODE_MAP } from 'constants/errorCodes';

// Helpers
import { getToken, isAuthenticated, setToken, getUserType } from 'helpers/auth';

// Actions
import { setOpen } from 'store/ducks/snackbars/actions';

// Services
import { auth } from 'services/firebase';
import { onAuthStateChanged, getIdToken } from 'firebase/auth';

// Store
import { history, persistor } from 'store';
import {
  setStorageTestOnly,
  getTestOnly,
  setStorageDaysLimitToReportDurable,
  getDaysLimitToReportDurable,
} from 'helpers/localConfig';
import {
  getStorageEndStop,
  getStorageLastPath,
  getStorageStartStop,
  setStorageEndStop,
  setStorageLastPath,
  setStorageStartStop,
} from 'helpers/ticketSales';

const apiBase = axios.create({
  baseURL: ENVIRONMENTS.API_DIGITAL_TICKET.BASE_URL?.replace(
    '.net/bilhetagem',
    '.net/3ds',
  ),
});

const userType = getUserType();

const callAPiBaseTo3DsAuth = (
  call: AxiosRequestConfig & {
    endpoint: string;
    method: AxiosRequestConfig;
    useToken: boolean;
    params?: {};
    data?: any;
    formData?: FormData;
    headers?: {};
    showJSON?: boolean;
    showConsoleLog?: boolean;
    title?: string;
  },
) => {
  const {
    endpoint,
    method = 'GET',
    useToken = false,
    params = {},
    data = {},
    formData,
    responseType = '',
  } = call;

  // Config Headers
  const defaultHeaders: {
    'Content-Type'?: string;
    Authorization?: string | null;
    'Ocp-Apim-Subscription-Key': string;
    'Ocp-Apim-Trace': boolean | string;
  } = {
    'Content-Type': formData ? 'multipart/form-data' : 'application/json',
    'Ocp-Apim-Subscription-Key': ENVIRONMENTS.API_DIGITAL_TICKET.OCP_APIM
      .SUBSCRIPTION_KEY!,
    'Ocp-Apim-Trace': ENVIRONMENTS.API_DIGITAL_TICKET.OCP_APIM.TRACE!,
  };

  if (useToken && isAuthenticated()) {
    defaultHeaders.Authorization = getToken();
  } else if (useToken && !isAuthenticated()) {
    console.error(
      'callApiBase - needed to use the token but it does not exist',
    );
  }

  let { headers } = call;
  headers = { ...defaultHeaders, ...headers };
  // End Config Headers

  // Reauth
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const idToken = await getIdToken(user);
        setToken(idToken);
      } catch (error) {
        console.error('Error retrieving ID token:', error);
      }
    }
  });

  apiBase.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response.status === 401) {
        setOpen({
          open: true,
          type: 'warning',
          message: errorMessagersList(CONST_AUTH.FIREBASE_FORBIDDEN),
        });

        const storageStartStop = getStorageStartStop();
        const storageEndStop = getStorageEndStop();
        const storageLastPath = getStorageLastPath();
        const storageTestOnly = getTestOnly();
        const storageDaysLimitToReportDurable = getDaysLimitToReportDurable();

        // !!! Clear all LocalStorage and Redux Persist !!!
        persistor.purge();
        localStorage.clear();

        setStorageStartStop(storageStartStop);
        setStorageEndStop(storageEndStop);
        setStorageLastPath(storageLastPath);
        setStorageTestOnly(storageTestOnly);
        setStorageDaysLimitToReportDurable(storageDaysLimitToReportDurable);

        if (
          history.location.pathname.includes(
            PATH.TICKET_RESERVATION_WEBSITE.BASE_URL,
          )
        ) {
          return history.push(PATH.TICKET_RESERVATION_WEBSITE.LOGIN);
        }

        const isCompany = userType === CONST_AUTH.TYPES.COMPANY.type;

        if (isCompany) {
          return history.push(PATH.COMPANY_MODULE.LOGIN);
        }

        return history.push(PATH.LOGIN);
      }

      if (
        error.response.status === 403 &&
        history.location.pathname !== PATH.NOT_ALLOWED.BASE_URL
      ) {
        history.replace(PATH.NOT_ALLOWED.BASE_URL, {
          fromPath: history.location.pathname,
        });
      }

      if (error.response.status === 404 && ERROR_CODE_MAP.has(error.response.data?.internalCode)) {
        const message =
          error.response?.data?.message || error.response?.message || '';

        const match = message.match(/:\s*(\S+)$/);
        const functionName = match ? match[1] : null;

        history.replace(PATH.FEATURE_DISABLED.BASE_URL, { functionName });
      }

      return Promise.reject(error);
    },
  );

  const storageTestOnly = JSON.parse(getTestOnly());

  if (method === 'GET' || method === 'DELETE') {
    let updateParams = params;
    if (endpoint.includes('/reports')) {
      updateParams = { ...params, testOnly: storageTestOnly };
    }
    return apiBase(endpoint, {
      params: {
        ...updateParams,
      },
      headers,
      method,
    });
  }

  let body;

  let updateData = data;
  if (endpoint.includes('/reports')) {
    updateData = { ...data, testOnly: storageTestOnly };
  }

  if (responseType) {
    body = {
      headers,
      method,
      params: {},
      data: updateData,
      responseType,
    };
  } else if (formData) {
    let updateFormData = formData;
    if (endpoint.includes('/reports')) {
      updateFormData = { ...data, testOnly: storageTestOnly };
    }
    body = {
      headers,
      method,
      params: {},
      data: updateFormData,
    };
  } else {
    body = {
      headers,
      method,
      params: {},
      data: updateData,
    };
  }

  return apiBase(endpoint, body);
};

export default callAPiBaseTo3DsAuth;
